// import npm libraries
import Link from 'next/link'
import { useEffect, useState } from 'react'
import dynamic from 'next/dynamic'
import Script from 'next/script'
import cn from 'classnames'

// import local components
import { IDeviceInfo, useUI } from '@components/ui/context'
import { IProductResponseWithResultArray } from './helpers/productResponse.model'
import { IImage } from '@components/utils/model/product.model'
import { getAppsFlyerDynamicLinkFromCookies, getCommonAppsFlyerValue, getCurrentPage, } from '@framework/utils/app-util'
import { GA_EVENT } from 'hooks/ga_event.modal'
import { useMenCollection } from './hook/menCollections.hook'
import { transformProductResult } from './helpers'
import { API_STATUS, AppsFlyerKeys, AppsFlyerParams, EmptyGuid, PAGE_TYPE_PLP, } from '@components/utils/constants'
import { recordGA4Event } from '@components/services/analytics/ga4'
import { OrangeSpinner } from '@components/icons'
import { useRouter } from 'next/router'
import ProductListStickyBar from './ProductListStickyBar'

import CategoryFilter from '@components/product/Filters/FilterCategory'
import ProductMobileFilters from '@components/product/Filters/CollectionMobileFilter'
import ProductFilterRight from '@components/product/Filters/CollectionFilters'
import ProductFiltersTopBar from '@components/product/Filters/FilterTopBar'
import ProductGridWithFacet from '@components/product/Grid'
import ProductGrid from '@components/product/Grid/ProductGrid'

declare const window: any
declare const AF: any

// dynamic imports
const ReactPlayerWrapper = dynamic(
  () => import('@components/product/ProductView/ReactPlayerWrapper'),
  { ssr: false }
)
const QuickViewModal = dynamic(
  () => import('@components/product/QuickView/ProductQuickView'),
  { ssr: false }
)

interface IProps {
  deviceInfo: IDeviceInfo
  customInfo1: string
  customInfo2: string
  customInfo3: string
  images: IImage[]
  id: string
  completeSlug: string
  products: IProductResponseWithResultArray
  previousPath: string
  allowFacets: boolean
  name: string
  description: string
  customLinks: string
  mid1Banners: IImage[]
  mid2Banners: IImage[]
  mid3Banners: IImage[]
}

const SLUG_HEADING_MAP: { [key: string]: string } = {
  'constant-500-days-collection' : "Explore Men's 500 Days Constant Collection From Damensch",
  'valuedeals/boxershorts' : "Premium Boxer Shorts for Men - Exclusive Value Deals",
  'valuedeals/innerboxers': "Premium Inner Boxers for Men - Exclusive Value Deals",
};

const renderSkeleton = (currentPage: number, productLength: number) => {
  return (
    <>
      {currentPage === 1 && productLength === 0 && (
        <section
          aria-labelledby="products-heading"
          className="mx-auto overflow-hidden max-w-7xl sm:px-6 lg:px-6"
        >
          <div
            className={`grid grid-cols-2 w-full sm:gap-y-1 sm:mr-3 sm:ml-3 md:grid-cols-4 product-listing-main lg:grid-cols-4`}
            style={{ margin: '0' }}
          >
            {Array.from({ length: 8 }, (_: any, index: number) => (
              <div key={index} className="relative mb-1">
                <div className="px-0 pt-0 mt-3 sm:px-0 ml-05 mr-05">
                  <div className="w-full h-[470px] rounded bg-slate-200" />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  )
}

export default function MenCollectionView(props: IProps) {
  const {
    customInfo1,
    customInfo2,
    customInfo3,
    deviceInfo,
    images,
    products,
    id,
    completeSlug,
    previousPath,
    allowFacets,
    name,
    description,
    customLinks,
    mid1Banners,
    mid2Banners,
    mid3Banners,
  } = props
  const tempWindow: any = typeof window !== 'undefined' ? window : undefined
  const tempAF: any = typeof AF !== 'undefined' ? AF : undefined
  const router = useRouter()

  const {
    totalResults,
    cls,
    hasAllItemsLoaded,
    swrLoading,
    state,
    productDataToPass,
    productListMemory,
    quickViewProductSlug,
    filtersLoader,
    setFiltersLoader,
    setQuickViewProductSlug,
    handlePageChange,
    handleInfiniteScroll,
    handleFilters,
    handleFiltersApply,
    handleSortBy,
    clearAll,
  } = useMenCollection({
    products,
    id,
    window: tempWindow,
    AF: tempAF,
    previousPath,
    images,
    customLinks,
    router,
  })
  const [pageName, setPageName] = useState<string>('')
  const dynamicLink = getAppsFlyerDynamicLinkFromCookies()

  const { user } = useUI()

  useEffect(() => {
    const currentPage = getCurrentPage()
    if (productDataToPass?.results?.length > 0) {
      const lastProductsLength =
        (productDataToPass?.results?.length / 20 - 1) * 20
      const newProductsList = new Array(...productDataToPass?.results)?.splice(
        lastProductsLength
      )
      let sectionName = getSelection()
      recordGA4Event(window, GA_EVENT.VIEW_ITEM_LIST, {
        ecommerce: {
          previous_page: previousPath,
          item_list_name: sectionName ? sectionName : '',
          items: newProductsList?.map((item: any, itemId: number) => {
            let color = ''
            if (item?.variantGroupCode) {
              color = item.variantGroupCode.split('-')[1]
            }
            return {
              item_name: item?.name,
              item_id: item?.sku,
              price: item?.price?.raw?.withTax,
              item_brand: item?.brand,
              item_category: item?.classification?.mainCategoryName,
              item_category2: item?.classification?.category,
              item_variant: color,
              color,
              item_list_name: props?.name,
              item_list_id: props?.id,
              index: itemId + 1,
              item_var_id: item?.stockCode,
            }
          }),
          loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
        },
      })
      if (typeof AF !== 'undefined') {
        const commonEventValue = getCommonAppsFlyerValue()
        AF('pba', 'event', {
          eventType: 'EVENT',
          eventName: AppsFlyerKeys.ViewItems,
          eventRevenueCurrency: 'INR',
          eventValue: {
            [AppsFlyerParams.AFEventParam3]: currentPage,
            [AppsFlyerParams.AFEventParamContent]: props?.name,
            [AppsFlyerParams.AFEventParamContentId]: props?.id,
            ...commonEventValue,
          },
        })
      }
    }
  }, [productDataToPass?.results?.length])

  useEffect(() => {
    const pageName = SLUG_HEADING_MAP[completeSlug] || '';
    setPageName(pageName);
  }, [completeSlug]);

  const isPlaying = true

  return (
    <>
      {filtersLoader === API_STATUS.LOADING ? (
        <div className="bg-black/[.5] cs-loader-box fixed top-0 right-0 z-50 flex items-center justify-center w-screen h-screen z-50">
          <OrangeSpinner className="animate-spin" />
        </div>
      ) : null}

      <div className={cls}>
        <div className="sticky w-full px-4 py-4 mx-auto bg-white top-108 index-2 sm:container sm:py-4 sm:px-6 lg:px-6">
          <div className="grid w-full grid-cols-1 border-gray-200 sm:pb-4 sm:border-b sm:grid-cols-12">
            <div className="mt-1 mr-2 sm:col-span-3 product-side-filter">
              <div className="text-base font-bold tracking-tight capitalize text-primary dark:text-primary text-24">
                <h1 className="inline-block text-2xl font-semibold tracking-tight capitalize text-primary dark:text-primary">
                  {name?.trim()?.length > 0 ? name : pageName}
                </h1>

                <span
                  className="pl-2 mt-0 text-xs font-light tracking-tight sm:h-6"
                  style={{
                    color: 'rgba(37, 16, 0, 0.64)',
                  }}
                >
                  {`${totalResults ?? 0} results`}
                </span>
              </div>
              <h2>{description}</h2>
            </div>
            {allowFacets && (
              <div className="hidden sm:col-span-9 product-card-list sm:block">
                <CategoryFilter
                  handleFilters={handleFilters}
                  customLinks={customLinks}
                  clearAll={clearAll}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-0 mx-auto sm:overflow-hidden sm:container sm:grid-cols-12">
        {allowFacets && (
          <>
            {/* {MOBILE FILTER PANEL SHOW ONLY IN MOBILE} */}
            {props?.customLinks?.length > 0 ? (
              <div
                className="sm:hidden sticky bg-white sort-top-mobile-sticky z-50 plp-categories-rail"
                id="categories-rail"
                style={{ paddingBottom: '0' }}
              >
                <CategoryFilter
                  handleFilters={handleFilters}
                  customLinks={props?.customLinks}
                  clearAll={clearAll}
                />
              </div>
            ) : null}
            <div
              className="sticky z-9 flex py-0 pr-0 bg-white sm:col-span-2 sm:hidden mobile-filter-form sort-top-mobile-sticky plp-filters-rail"
              id="filters-rail"
            >
              <ProductMobileFilters
                handleFilters={handleFilters}
                products={productDataToPass}
                routerFilters={state?.filters}
                handleSortBy={() => {}}
                clearAll={clearAll}
                routerSortOption={state?.sortBy}
                handleFiltersApply={handleFiltersApply}
                swrLoading={swrLoading}
                deviceInfo={deviceInfo}
                setFiltersLoader={setFiltersLoader}
              />
            </div>
            {/* <div className="flex flex-col px-4 sm:hidden sm:px-6 lg:px-6">
                <ProductMobileFilter
                  handleFilters={handleFilters}
                  products={products}
                  routerFilters={state?.filters}
                  handleSortBy={handleSortBy}
                  clearAll={clearAll}
                  routerSortOption={state?.sortBy}
                />
              </div> */}
            <div className="hidden px-4 sm:col-span-2 sm:px-6 lg:px-6 sm:block product-side-filter">
              <ProductFilterRight
                handleFilters={handleFilters}
                products={productDataToPass}
                routerFilters={state?.filters}
                clearAll={clearAll}
                swrLoading={swrLoading}
              />
            </div>
            <div className="sm:col-span-10 product-card-list">
              {/* {HIDE FILTER TOP BAR IN MOBILE} */}

              {customInfo2 != '' && (
                <>
                  <div className="mx-auto overflow-hidden max-w-7xl plp-video-banner">
                    <ReactPlayerWrapper
                      videoId=""
                      isPlaying={isPlaying}
                      videoUrl={customInfo2}
                    />
                  </div>
                </>
              )}
              <div className="flex-1 hidden px-4 sm:block sm:px-6 lg:px-6">
                <ProductFiltersTopBar
                  products={transformProductResult(productListMemory)}
                  handleSortBy={handleSortBy}
                  routerFilters={state?.filters}
                  clearAll={clearAll}
                  routerSortOption={state?.sortBy}
                  swrLoading={swrLoading}
                />
              </div>
              <ProductGridWithFacet
                products={productDataToPass}
                currentPage={state?.currentPage}
                handlePageChange={handlePageChange}
                handleInfiniteScroll={handleInfiniteScroll}
                collectionName={name}
                collectionId={id}
                routerFilters={state?.filters}
                deviceInfo={deviceInfo}
                // pageType='PLP'
                pageType={PAGE_TYPE_PLP}
                dynamicLink={dynamicLink}
                setQuickViewProductSlug={(payload: string | null) => {
                  setQuickViewProductSlug(payload)
                }}
                mid1Banners={mid1Banners}
                mid2Banners={mid2Banners}
                mid3Banners={mid3Banners}
              />
            </div>
          </>
        )}
        {!allowFacets && (
          <>
            <div className="col-span-12">
              <ProductGrid
                products={productDataToPass}
                currentPage={state?.currentPage}
                handlePageChange={handlePageChange}
                handleInfiniteScroll={handleInfiniteScroll}
                collectionName={name}
                collectionId={id}
                deviceInfo={deviceInfo}
                // pageType='PLP'
                pageType={PAGE_TYPE_PLP}
                setQuickViewProductSlug={(payload: string | null) => {
                  setQuickViewProductSlug(payload)
                }}
              />
            </div>
          </>
        )}
        <div></div>
      </div>
      {customInfo1 != '' && (
        <div
          className={cn('flex flex-col w-full', {
            hidden: !hasAllItemsLoaded,
            block: hasAllItemsLoaded,
          })}
        >
          <div className="flex flex-col">
            <div className="section-devider"></div>
          </div>
          <div className="px-4 mx-auto sm:container page-container sm:px-6">
            <div
              className="mb-4 text-xs font-normal text-brown seo-info custom-html"
              dangerouslySetInnerHTML={{ __html: customInfo1 }}
            />
          </div>
        </div>
      )}
      {productDataToPass?.results?.length && (
        <div className="mobile-tab ">
          <ProductListStickyBar
            elementTagId={`${productDataToPass?.results?.[0]?.recordId}-plp-product`}
            elementTagClass={'quickView-plp-product'}
            firstProductRecordId={productDataToPass?.results?.[0]?.recordId}
            deviceInfo={deviceInfo}
            previousPath={previousPath}
          />
        </div>
      )}
      {quickViewProductSlug && (
        <QuickViewModal
          quickViewProductSlug={quickViewProductSlug}
          setQuickview={(payload: string | null) => {
            setQuickViewProductSlug(payload)
          }}
          collectionName={`You May Also Like`}
          deviceInfo={deviceInfo}
          title={`PLP`}
          page={`PLP Quick view`}
        />
      )}

      {products?.results?.length > 0 && (
        <Script
          type="application/ld+json"
          id="schema"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
                {
                  "@context": "https://schema.org/",
                  "@type": "ItemList",
                  "itemListElement": ${JSON.stringify(
                    products?.results?.map((product: any, pId: number) => ({
                      '@type': 'ListItem',
                      position: pId + 1,
                      name: product?.name,
                      url: 'https://www.damensch.com/' + product?.slug,
                    }))
                  )}
                }
              `,
          }}
        />
      )}
    </>
  )
}
